import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSupportLogos,
  createSupportLogo,
  updateSupportLogo,
  deleteSupportLogo,
  reset
} from '../../features/supportLogos/supportLogosSlice';
import { useToast } from '../../contexts/ToastContext';
import { getImageUrl } from '../../utils/imageUrl';

const ManajemenSupportLogo = () => {
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const { logos, isLoading, isSuccess, isError, message } = useSelector(
    (state) => state.supportLogos
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    link: '',
    order: 0,
    logo: null
  });

  useEffect(() => {
    dispatch(getSupportLogos());
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess) {
      addToast({ type: 'success', message: 'Operasi berhasil!' });
      resetForm();
      setIsModalOpen(false);
      setEditingLogo(null);
    }
    if (isError) {
      addToast({ type: 'error', message: message });
    }
    dispatch(reset());
  }, [isSuccess, isError, message, dispatch, addToast]);

  const resetForm = () => {
    setFormData({
      name: '',
      link: '',
      order: 0,
      logo: null
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('name', formData.name);
    data.append('link', formData.link);
    data.append('order', formData.order);
    if (formData.logo) {
      data.append('logo', formData.logo);
    }

    if (editingLogo) {
      dispatch(updateSupportLogo({ id: editingLogo._id, logoData: data }));
    } else {
      dispatch(createSupportLogo(data));
    }
  };

  const handleEdit = (logo) => {
    setEditingLogo(logo);
    setFormData({
      name: logo.name,
      link: logo.link,
      order: logo.order,
      logo: null
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Yakin ingin menghapus logo ini?')) {
      dispatch(deleteSupportLogo(id));
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manajemen Support Logo</h1>
        <button
          onClick={() => {
            resetForm();
            setEditingLogo(null);
            setIsModalOpen(true);
          }}
          className="px-6 py-2 bg-dark-blue-700 text-white rounded-xl hover:bg-dark-blue-800 transition-colors"
        >
          Tambah Logo
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {logos.map((logo) => (
          <div key={logo._id} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-center h-32 mb-4 bg-gray-50 rounded-xl">
              <img
                src={getImageUrl(logo.logo)}
                alt={logo.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{logo.name}</h3>
            <a
              href={logo.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-dark-blue-600 hover:text-dark-blue-800 mb-4 block truncate"
            >
              {logo.link}
            </a>
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(logo)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(logo._id)}
                className="flex-1 px-4 py-2 bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-colors"
              >
                Hapus
              </button>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              {editingLogo ? 'Edit Logo' : 'Tambah Logo Baru'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nama</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link</label>
                <input
                  type="url"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Urutan</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, logo: e.target.files[0] })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500"
                  required={!editingLogo}
                />
                {editingLogo && (
                  <p className="text-sm text-gray-500 mt-2">Biarkan kosong jika tidak ingin mengubah logo</p>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                    setEditingLogo(null);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-dark-blue-700 text-white rounded-xl hover:bg-dark-blue-800 transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManajemenSupportLogo;
